import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMachine } from "@xstate/react";
import { useSnackbar } from "notistack";
import { useQueryClient } from "@tanstack/react-query";
import { CACHE_KEYS } from "@bn/api/apiv1";
import { LoadingPlaceHolder } from "@components";
import { SomethingWrong } from "../transactionWizard/steps/SomethingWrong";
import { BookingWizardHeader } from "../transactionWizard/components/BookingWizardHeader";
import { useStateHelpers } from "./hooks";
import { ConfirmManualDividendStep } from "./steps/ConfirmManualDividendStep";
import { AddManualDividendStep } from "./steps/AddManualDividendStep";
import { DividendValues } from "./stateMachine/types/dividendValues";
import { editDividendWizardSM } from "./stateMachine/editDividendWizardSM";

export interface Props {
  investmentId: number;
  bookingId: number;
  image?: string | null;
  onSuccess?: () => void;
}

export const EditDividendWizard = ({
  investmentId,
  bookingId,
  image,
  onSuccess,
}: Props) => {
  const { t } = useTranslation();
  const [currentState, sendEvent] = useMachine(editDividendWizardSM, {
    context: { bookingId },
  });
  const { isLoadingStateForModals } = useStateHelpers(currentState);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  useEffect(() => {
    sendEvent({ type: "INIT_FROM_INVESTMENT", investmentId });
  }, [investmentId, sendEvent]);

  const investment = currentState.context.investment;
  const investmentName = investment?.name ?? "";
  const originalBooking = currentState.context.booking;
  const bookingType =
    originalBooking?.type ?? currentState.context.dividendValues?.type;
  const handleBackStep = () => sendEvent({ type: "BACK" });
  const isSuccess = currentState.matches("success");
  const isPersisting = currentState.matches("persist");
  const isValidating = currentState.matches("validateDividends");

  const header = (
    <BookingWizardHeader
      name={investment?.name}
      isin={investment?.isin}
      wkn={investment?.wkn}
      type={investment?.type}
      image={image}
      bookingType={bookingType}
    />
  );

  useEffect(() => {
    if (!isSuccess) return;
    enqueueSnackbar(
      `${t("transactionWizard.persistedStep.title")}: ${investmentName}`,
      { autoHideDuration: 3000, variant: "success" }
    );
    queryClient.invalidateQueries({
      queryKey: [CACHE_KEYS.LIST_AND_SEARCH_ALL_BOOKINGS],
    });
    queryClient.invalidateQueries({
      queryKey: [CACHE_KEYS.LIST_AND_SEARCH_ALL_INVESTMENTS],
    });
    onSuccess?.();
  }, [isSuccess, enqueueSnackbar, queryClient, t, investmentName, onSuccess]);

  return (
    <>
      {isLoadingStateForModals() && <LoadingPlaceHolder height="300px" />}

      {(currentState.matches("editManualDividend") || isValidating) && (
        <AddManualDividendStep
          onNext={(dividendValues: DividendValues) =>
            sendEvent({
              type: "DIVIDEND_FILLED",
              dividendValues,
            })
          }
          dividendValues={currentState.context.dividendValues}
          investmentName={currentState.context.investment?.name ?? ""}
          investmentCurrency={
            currentState.context.investment?.currency ?? "EUR"
          }
          validationErrors={currentState.context.validationErrors ?? []}
          headerContent={header}
          isFetching={isValidating}
        />
      )}

      {(currentState.matches("summary") || isPersisting) && (
        <ConfirmManualDividendStep
          dividendValues={currentState.context.dividendValues}
          investmentCurrency={
            currentState.context.investment?.currency ?? "EUR"
          }
          investmentName={currentState.context.investment?.name ?? ""}
          onPrevious={handleBackStep}
          onNext={() => sendEvent({ type: "PERSIST" })}
          headerContent={header}
          isFetching={isPersisting}
        />
      )}

      {currentState.matches("somethingWrong") && (
        <SomethingWrong message={currentState.context.error?.message} />
      )}
    </>
  );
};
